import React, { useState, useEffect } from 'react';
import { Layout, Dropdown, Avatar, message, Space, Badge, Modal, Input } from 'antd';
import { 
  UserOutlined, 
  LogoutOutlined, 
  SettingOutlined, 
  BellOutlined,
  SearchOutlined,
  FullscreenOutlined
} from '@ant-design/icons';
import AuthService from '../../utils/auth';

const { Header } = Layout;

const Topbar = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(() => 
    localStorage.getItem('darkMode') === 'true'
  );
  const [searchVisible, setSearchVisible] = useState(false);
  const [notifications] = useState([
    { id: 1, title: '新订单', message: '您有一个新的订单需要处理', time: '2分钟前' },
    { id: 2, title: '系统通知', message: '系统将于今晚23:00进行维护', time: '1小时前' },
    { id: 3, title: '库存预警', message: '商品"龙井茶"库存不足', time: '3小时前' }
  ]);

  useEffect(() => {
    // 获取用户信息
    const user = AuthService.getUserInfo();
    setUserInfo(user);

    // 监听全屏状态变化
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    // 应用暗色主题
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [darkMode]);

  // 全屏切换
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // 切换暗色模式
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    localStorage.setItem('darkMode', !darkMode);
  };

  // 搜索功能
  const handleSearch = (value) => {
    if (value.trim()) {
      message.info(`搜索: ${value}`);
      // 这里可以实现全局搜索逻辑
    }
    setSearchVisible(false);
  };

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      message.success('已退出登录');
    window.location.href = '/login';
    } catch (error) {
      console.error('登出失败:', error);
      message.error('登出失败，请重试');
    }
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
      onClick: () => {
        // 跳转到个人资料页面
        window.location.href = '/settings/profile';
      }
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      onClick: () => {
        // 跳转到系统设置页面
        window.location.href = '/settings/basic';
      }
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout
    }
  ];

  // 通知菜单
  const notificationMenuItems = [
    ...notifications.map(notification => ({
      key: notification.id,
      label: (
        <div style={{ maxWidth: 300, padding: '8px 0' }}>
          <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>
            {notification.title}
          </div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
            {notification.message}
          </div>
          <div style={{ fontSize: 11, color: '#999' }}>
            {notification.time}
          </div>
        </div>
      )
    })),
    {
      type: 'divider'
    },
    {
      key: 'view-all',
      label: '查看所有通知',
      onClick: () => {
        // 跳转到通知页面
        window.location.href = '/notifications';
      }
    }
  ];

  return (
    <Header
      style={{
        minWidth: 0,
        background: 'rgba(255,255,255,.94)',
        backdropFilter: 'blur(18px)',
        padding: '0 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        height: '56px',
        boxShadow: 'none',
        borderBottom: '1px solid #e4eae7',
        position: 'relative',
        zIndex: 10
      }}
    >
      {/* 全局操作区 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <Space size={16}>
          <SearchOutlined style={{ fontSize: '18px', cursor: 'pointer', color: '#8c8c8c' }} onClick={() => setSearchVisible(true)} />
          
          <Badge count={notifications.length} size="small" offset={[2, 0]}>
            <BellOutlined style={{ fontSize: '18px', cursor: 'pointer', color: '#8c8c8c' }} />
          </Badge>

          <div style={{ width: '1px', height: '16px', background: '#f0f0f0' }} />

          <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <Avatar 
                size={32} 
                style={{ backgroundColor: '#1d3a2f' }}
                icon={<UserOutlined />} 
              />
              <span style={{ fontSize: '14px', fontWeight: '500' }}>{userInfo?.username || 'Admin'}</span>
            </div>
          </Dropdown>
        </Space>
      </div>
      
      {/* 搜索模态框 */}
      <Modal
        title="全局搜索"
        open={searchVisible}
        onCancel={() => setSearchVisible(false)}
        footer={null}
        centered
        width={600}
      >
        <Input.Search
          placeholder="搜索商品、订单、用户..."
          enterButton="搜索"
          size="large"
          onSearch={handleSearch}
          autoFocus
        />
        <div style={{ marginTop: 16, color: '#666', fontSize: 12 }}>
          提示：支持搜索商品名称、订单号、用户昵称等
        </div>
      </Modal>
    </Header>
  );
};

export default Topbar;